import { Organization, User, UserModel, ValidationError } from '../../gs-core/common';

import { Subject, SubjectModel } from './Subject';
import { DocFormat, DocFormats } from './DocFormat';
import { DocStatus, DocStatuses } from './DocStatus';
import { ViewAccess, ViewAccessTypes } from './ViewAccess';
import { SignedUpload } from './SignedUpload';
import { SubjectType } from './SubjectType';
import { Problems } from './Problems';

export const MAX_TITLE_LENGTH = 120;
export const MAX_PAGE_SIZE = 1000;
export const DEFAULT_PAGE_SIZE = 12;

export interface Doc {
  id: number;
  url?: string;
  eTag?: string;         // S3 hash of the file
  versionId?: string;    // S3 version ID for the file
  thumbnail?: { url?: string; };
  signedUpload?: SignedUpload;
  subject?: Subject;
  title?: string;
  titleChangedDate: Date;
  details?: string;
  format: DocFormat;
  status: DocStatus;
  owningUnit: Organization;
  viewAccess: ViewAccess;
  createdBy: User;
  createdDate: Date;
  modifiedBy: User;
  modifiedDate: Date;
  prmSyncDate?: Date;
  deleteDate?: Date;
  problems?: Problems;
  getRoute: () => string;
  sameAccess: (otherDoc: Doc) => boolean;
  toJSON: () => Partial<Doc>;
  toString: () => string;
}

export interface DocIndex {
  id: number;
  subjectUserId?: number;
  subjectName?: string;
  subjectType?: SubjectType;
  title: string;
  titleRaw: string;
  titleChangedDate: Date;
  details?: string;
  status: DocStatus;
  owningUnitId: number;
  createdById: number;
  createdDate: Date;
}

export interface DocContent {
  id: number;
  content: string;
}

export const DocModel = {
  create(props: Partial<Doc>): Doc {
    if (!props) {
      throw new Error('Missing properties to create Doc');
    }

    const viewAccess = props.viewAccess ? ViewAccessTypes.parse(props.viewAccess) : ViewAccessTypes.SUBJECTS_GROUPS;

    const createdBy = UserModel.create(props.createdBy as User);

    if (!createdBy) {
      throw new Error('Missing createdBy');
    }

    if (!props.createdDate) {
      throw new Error('Missing createdDate');
    }

    const modifiedBy = UserModel.create(props.modifiedBy as User);

    if (!modifiedBy) {
      throw new Error('Missing modifiedBy');
    }

    if (!props.modifiedDate) {
      throw new Error('Missing modifiedDate');
    }

    const owningUnit = UserModel.create(props.owningUnit as User) as Organization;

    if (!owningUnit) {
      throw new Error('Missing owningUnit');
    }

    const subject = props.subject && SubjectModel.create(props.subject, viewAccess);
    const title = props.title && props.title.trim();

    // todo - pull out into a validate function and scrub it too
    if (title && title.length > MAX_TITLE_LENGTH) {
      throw new ValidationError(`Title of ${title.length} characters is too long`);
    }

    const status = props.status ? DocStatuses.parse(props.status) :
        (owningUnit && subject && title) ? DocStatuses.FILED : DocStatuses.UNFILED;

    return {
      id: props.id || 0,
      url: props.url,
      eTag: props.eTag,
      versionId: props.versionId,
      thumbnail: props.thumbnail,
      signedUpload: props.signedUpload,
      subject,
      title: props.title,
      titleChangedDate: props.titleChangedDate || props.modifiedDate,
      format: DocFormats.parse(props.format as DocFormat),
      status,
      details: props.details,
      owningUnit,
      viewAccess,
      createdBy,
      createdDate: new Date(props.createdDate),
      modifiedBy,
      modifiedDate: new Date(props.modifiedDate),
      // todo - only add this if it actually was sync'd?
      prmSyncDate: props.prmSyncDate && new Date(props.prmSyncDate),
      deleteDate: props.deleteDate && new Date(props.deleteDate),
      problems: props.problems && { ...props.problems },
      getRoute,
      sameAccess,
      toJSON,
      toString
    };
  }
};

function getRoute(this: Doc): string {
  return `/doc/${this.id}`;
}

function sameAccess(this: Doc, otherDoc: Doc): boolean {
  if (!otherDoc) {
    return false;
  }

  const userId = this.subject && this.subject.user.id;
  const otherUserId = otherDoc.subject && otherDoc.subject.user.id;
  const subjectType = this.subject && this.subject.type;
  const otherSubjectType = otherDoc.subject && otherDoc.subject.type;

  return this.id !== otherDoc.id || this.owningUnit.id !== otherDoc.owningUnit.id || userId !== otherUserId ||
    subjectType !== otherSubjectType;
}

function toString(this: Doc): string {
  return `Doc(id: ${this.id}, subject: ${this.subject && this.subject.user.name})`;
}

function toJSON(this: Doc): Partial<Doc> {
  return {
    id: this.id,
    url: this.url,
    eTag: this.eTag,
    versionId: this.versionId,
    thumbnail: this.thumbnail,
    signedUpload: this.signedUpload,
    subject: this.subject && this.subject.toJSON && (this.subject.toJSON() as Subject),
    title: this.title,
    details: this.details,
    format: this.format,
    status: this.status,
    owningUnit: this.owningUnit && {
      id: this.owningUnit.id,
      code: this.owningUnit.code,
      name: this.owningUnit.name,
      clazz: this.owningUnit.clazz
    },
    viewAccess: this.viewAccess,
    createdBy: {
      id: this.createdBy.id,
      name: this.createdBy.name,
      clazz: this.createdBy.clazz
    },
    createdDate: this.createdDate,
    modifiedBy: {
      id: this.modifiedBy.id,
      name: this.modifiedBy.name,
      clazz: this.modifiedBy.clazz
    },
    modifiedDate: this.modifiedDate,
    prmSyncDate: this.prmSyncDate,
    deleteDate: this.deleteDate,
    problems: this.problems && { ...this.problems }
  };
}



// WEBPACK FOOTER //
// ./src/common/models/Doc.ts